export const CATALOG_PERSON_SEARCH_MODE = {
  ASAN: "asan",
  MANUAL: "manual",
};

export function emptyPersonSearchDraft() {
  return {
    pin: "",
    docSeries: "",
    docNumber: "",
    searchMode: CATALOG_PERSON_SEARCH_MODE.ASAN,
  };
}

function pick(row, ...keys) {
  if (!row || typeof row !== "object") return null;
  for (const key of keys) {
    const v = row[key];
    if (v == null) continue;
    if (typeof v === "object") continue;
    if (String(v).trim() === "") continue;
    return v;
  }
  return null;
}

function messageFromRow(row) {
  if (row == null) return "";
  if (typeof row === "string") return row.trim();
  if (typeof row !== "object") return String(row).trim();

  if (row.msgText != null && String(row.msgText).trim()) return String(row.msgText).trim();
  if (row.message != null) {
    const nested = messageFromRow(row.message);
    if (nested) return nested;
  }
  return "";
}

export function extractPersonInfoErrorMessage(response) {
  const body = response?.data;
  if (!body) return "";

  const errors = body.errors ?? body.Errors;
  if (Array.isArray(errors)) {
    const parts = errors
      .map((e) => (typeof e === "string" ? e.trim() : messageFromRow(e)))
      .filter(Boolean);
    if (parts.length) return parts.join(", ");
  } else if (errors) {
    const one = typeof errors === "string" ? errors.trim() : messageFromRow(errors);
    if (one) return one;
  }

  const raw = body.data ?? body.Data;
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (isMessageShape(item)) {
        const m = messageFromRow(item);
        if (m) return m;
      }
    }
  }

  return messageFromRow(body.message ?? body.Message);
}

function isMessageShape(obj) {
  if (!obj || typeof obj !== "object") return false;
  const hasMsgFields = obj.msgText != null || obj.msgCode != null || obj.msgType != null;
  const hasPersonFields = Boolean(
    pick(obj, "firstName", "FirstName", "name", "Name", "lastName", "LastName", "surname", "idCode", "IdCode", "pin", "Pin")
  );
  return hasMsgFields && !hasPersonFields;
}

/** Fixed GetPersonInfo fields — only idCode / idSeries / idNumber change per search. */
const PERSON_INFO_FIXED = {
  countryId: 1,
  personType: 1,
  mode: 0,
  identificationType: "string",
  identificationTypeValue: 0,
};

export const PERSON_INFO_TEST_ID_SERIES = "AA";
export const PERSON_INFO_TEST_ID_NUMBER = "0000000";

export function validatePersonInfoSearchInput(pin, idSeries, idNumber) {
  const pinNorm = pin != null ? String(pin).trim() : "";
  const series = idSeries != null ? String(idSeries).trim() : "";
  const number = idNumber != null ? String(idNumber).trim() : "";
  return {
    ok: Boolean(pinNorm),
    pin: pinNorm,
    idSeries: series,
    idNumber: number,
    hasIdDoc: Boolean(series && number),
  };
}

function asRequestString(value) {
  if (value == null || value === "") return "";
  return String(value).trim();
}

export function resolvePersonInfoIdDoc(extra = {}) {
  const fromForm = {
    series: asRequestString(extra.idSeries),
    number: asRequestString(extra.idNumber),
  };
  const usedTestDefaults = !fromForm.series || !fromForm.number;
  return {
    idSeries: fromForm.series || PERSON_INFO_TEST_ID_SERIES,
    idNumber: fromForm.number || PERSON_INFO_TEST_ID_NUMBER,
    usedTestDefaults,
    hasIdDoc: Boolean(fromForm.series && fromForm.number),
  };
}

export function buildPersonInfoRequest(pin, extra = {}) {
  const idDoc = resolvePersonInfoIdDoc(extra);

  return {
    idCode: asRequestString(pin),
    idSeries: idDoc.idSeries,
    idNumber: idDoc.idNumber,
    ...PERSON_INFO_FIXED,
    _usedTestIdDoc: idDoc.usedTestDefaults,
  };
}

export function omitTestIdDocFromMapped(mapped, usedTestIdDoc) {
  if (!usedTestIdDoc || !mapped) return mapped;
  if (mapped.firstName || mapped.lastName) return mapped;
  const out = { ...mapped };
  if (out.docSeries === PERSON_INFO_TEST_ID_SERIES) delete out.docSeries;
  if (out.docNumber === PERSON_INFO_TEST_ID_NUMBER) delete out.docNumber;
  return out;
}

export function extractPersonInfoRow(response) {
  const body = response?.data;
  if (!body || typeof body !== "object") return null;
  if (body.isSuccess === false) return null;

  const candidates = [
    body.data,
    body.Data,
    body.result,
    body.person,
    body.personInfo,
    body.PersonInfo,
    body.client,
    body.Client,
  ];

  for (const raw of candidates) {
    if (raw == null) continue;

    if (Array.isArray(raw)) {
      for (const item of raw) {
        if (isPersonInfoFound(item)) return item;
      }
      continue;
    }

    if (typeof raw === "object") {
      const nested =
        raw.person ??
        raw.personInfo ??
        raw.PersonInfo ??
        raw.insured ??
        raw.client ??
        raw.Client ??
        null;
      if (isPersonInfoFound(nested)) return nested;
      if (isPersonInfoFound(raw)) return raw;
    }
  }

  if (isPersonInfoFound(body) && !isApiEnvelope(body)) return body;
  return null;
}

function isApiEnvelope(body) {
  return (
    body &&
    typeof body === "object" &&
    ("isSuccess" in body || "IsSuccess" in body) &&
    !pick(body, "firstName", "FirstName", "name", "Name", "surname", "idCode", "IdCode", "pin", "Pin")
  );
}

export function isPersonInfoFound(row) {
  if (row == null) return false;
  if (typeof row === "string") return row.trim() !== "";
  if (typeof row !== "object") return true;
  if (isMessageShape(row)) return false;
  return Object.keys(row).length > 0;
}

function normalizeSex(raw) {
  if (raw == null || raw === "") return "m";
  if (typeof raw === "number") {
    if (raw === 2) return "f";
    if (raw === 1) return "m";
    return "m";
  }
  const s = String(raw).trim().toLowerCase();
  if (s === "f" || s === "female" || s === "q" || s === "2") return "f";
  return "m";
}

function mapAddressToForm(addr) {
  if (!addr || typeof addr !== "object") return {};
  const custom = pick(addr, "customAddress", "CustomAddress");
  if (custom) {
    return { address: String(custom).trim() };
  }
  const out = {};
  if (addr.country != null && addr.country !== "") out.countryId = addr.country;
  if (addr.region != null && addr.region !== "") out.townId = addr.region;
  const street = pick(addr, "street", "Street");
  const house = pick(addr, "houseNr", "HouseNr", "house", "House");
  const apt = pick(addr, "apartmentNr", "ApartmentNr", "apt", "Apt");
  if (street) out.street = street;
  if (house) out.house = house;
  if (apt) out.apt = apt;
  const postal = pick(addr, "postalIndex", "PostalIndex", "zip", "Zip");
  if (postal) {
    const z = String(postal).trim();
    out.zip = /^AZ/i.test(z) ? z.slice(2) : z;
  }
  return out;
}

function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Map API person row → PersonEditDialog `values` fields (UniPolicy / ASAN shape). */
export function mapPersonInfoToForm(row) {
  const addr =
    row?.address && typeof row.address === "object" ? row.address : null;

  const mapped = {
    pin: pick(row, "pin", "Pin", "idCode", "IdCode"),
    firstName: pick(row, "firstName", "FirstName", "name", "Name", "ad", "Ad"),
    lastName: pick(row, "lastName", "LastName", "surname", "Surname", "surName", "SurName", "soyad", "Soyad"),
    middleName: pick(
      row,
      "middleName",
      "MiddleName",
      "patronymic",
      "Patronymic",
      "fatherName",
      "FatherName",
      "ata",
      "Ata"
    ),
    sex: normalizeSex(row.gender ?? row.Gender ?? pick(row, "sex", "Sex", "cins", "Cins")),
    dateOfBirth: parseDate(
      pick(row, "dob", "Dob", "dateOfBirth", "DateOfBirth", "birthDate", "BirthDate", "birthday", "Birthday")
    ),
    mobilePhone: pick(
      row,
      "phoneNumber",
      "PhoneNumber",
      "phoneMob",
      "PhoneMob",
      "mobilePhone",
      "MobilePhone",
      "phone",
      "Phone",
      "mobPhone",
      "MobPhone"
    ),
    email: pick(row, "email", "Email"),
    homePhone: pick(row, "homePhone", "HomePhone"),
    officePhone: pick(row, "officePhone", "OfficePhone"),
    docSeries: pick(row, "idSeries", "IdSeries", "policySeries", "PolicySeries", "docSeries", "DocSeries"),
    docNumber: pick(row, "idNumber", "IdNumber", "policyNumber", "PolicyNumber", "docNumber", "DocNumber"),
    countryId: pick(row, "countryId", "CountryId"),
    townId: pick(row, "townId", "TownId"),
    ...mapAddressToForm(addr),
  };

  if (!mapped.address) {
    if (!mapped.street) {
      mapped.street = pick(row, "street", "Street");
    }
    if (!mapped.house) {
      mapped.house = pick(row, "house", "House", "houseNr", "HouseNr");
    }
    if (!mapped.apt) {
      mapped.apt = pick(row, "apt", "Apt", "apartment", "Apartment", "apartmentNr", "ApartmentNr");
    }
    if (!mapped.zip) {
      mapped.zip = pick(row, "zip", "Zip", "postalCode", "PostalCode", "postalIndex", "PostalIndex");
    }
  }

  return Object.fromEntries(
    Object.entries(mapped).filter(([, v]) => v != null && v !== "")
  );
}
